/**
 * Where the door sits on a corner cabinet's visible face.
 *
 * Both corner variants show the same thing to the room: a span of the face
 * another cabinet stands across — blind, unreachable, no door — and the
 * remainder, which carries ONE door with its pull on the edge away from the
 * blind span.
 *
 *   - L-shaped (`buildCornerCabinet`): the perpendicular leg's end panel
 *     covers `cornerBox`, always at the −X end. The unit is seated FROM the
 *     room corner along +X, so the corner is always the low end.
 *   - single-sided / blind (`buildSingleSidedCornerCabinet`): the
 *     perpendicular run stands across `blindDepth` at the end `blindEnd`
 *     names — which is 'left' or 'right' depending on which way the run's leg
 *     extends, so this one is NOT always the low end. That is the fact three
 *     separate drawings used to each decide for themselves.
 *
 * Coordinates run along the unit's local +X — the host wall's
 * `bottomLeft → bottomRight` direction — from 0 at the −X end to `width` at
 * the +X end. The 3D renderer, the 2D plan and the elevation schematic all
 * ask here and map 0..width onto their own frame (and the elevation mirrors
 * when the wall's drawing basis opposes the wall direction).
 *
 * Pure. Metres throughout.
 */

const { CORNER_BOX } = require('./buildCornerCabinet');

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * @param {Object} dimensions - the unit's `dimensions` bag. Accepts either
 *   spelling of the box (`x`/`z` as stored, `width`/`depth` as the 3D
 *   renderer names them) plus the variant metadata the builders write:
 *   `cornerVariant`, `blindEnd`, `blindDepth`, `cornerBox`.
 * @param {number} [drawnWidth] - width the caller is drawing at, when that is
 *   not the stored one (the elevation sizes its SVG from the projection).
 * @returns {{
 *   width: number, blindWidth: number, blindAtLowX: boolean,
 *   doorStart: number, doorEnd: number, doorWidth: number,
 *   pullAtHighX: boolean
 * } | null} null when there is no width to divide.
 */
function cornerCabinetFace(dimensions = {}, drawnWidth = null) {
  const width =
    num(drawnWidth) ?? num(dimensions.width) ?? num(dimensions.x) ?? 0;
  if (!(width > 0)) return null;

  let blindWidth;
  let blindAtLowX;
  if (dimensions.cornerVariant === 'single-sided') {
    const end = dimensions.blindEnd;
    // A unit that doesn't name its blind end has no blind span to draw: one
    // door across the whole face, rather than a guess that lands the door
    // inside the neighbouring cabinet half the time.
    blindAtLowX = end === 'left';
    blindWidth =
      end === 'left' || end === 'right'
        ? // The neighbour's depth, which is the builder's own default when it
          // isn't told otherwise (a corner is one run, built at one depth).
          (num(dimensions.blindDepth) ??
          num(dimensions.depth) ??
          num(dimensions.z) ??
          0)
        : 0;
  } else {
    blindAtLowX = true;
    blindWidth = num(dimensions.cornerBox) ?? CORNER_BOX;
  }
  blindWidth = Math.min(Math.max(blindWidth, 0), width);

  const doorStart = blindAtLowX ? blindWidth : 0;
  const doorEnd = blindAtLowX ? width : width - blindWidth;
  return {
    width,
    blindWidth,
    blindAtLowX,
    doorStart,
    doorEnd,
    doorWidth: doorEnd - doorStart,
    // The reachable edge: the one the blind span is NOT against. With no
    // blind span the door is a plain one — keep the pull at the +X edge, the
    // side the L variant has always carried it on.
    pullAtHighX: blindWidth > 0 ? blindAtLowX : true,
  };
}

// The reveal around a corner unit's door, each side, and the narrowest door
// the renderer builds: a leg no wider than the corner box leaves a sliver
// that is no door at all.
const CORNER_DOOR_INSET_M = 0.003;
const MIN_CORNER_DOOR_M = 0.005;

/**
 * Does this face carry its door — and with it the unit's ONE pull? The 3D
 * renderer draws the pull under exactly this test, and the estimate counts
 * the pull by it (`cabinet-runs/cabinetHardware.js`).
 */
const cornerFaceHasDoor = (face) =>
  !!face && face.doorWidth - CORNER_DOOR_INSET_M * 2 > MIN_CORNER_DOOR_M;

module.exports = {
  cornerCabinetFace,
  cornerFaceHasDoor,
  CORNER_DOOR_INSET_M,
  MIN_CORNER_DOOR_M,
};
